@if (!drawerMode) {
  <app-page-breadcrumb [pageTitle]="pageTitle"></app-page-breadcrumb>
}

<ng-container
  *ngIf="drawerMode; then formBody; else pageWrapper"
></ng-container>

<ng-template #pageWrapper>
  <div class="grid grid-cols-1 gap-6">
    <app-component-card
      [title]="pageTitle"
      desc="Defina la lista de precios y sus items"
    >
      <ng-container *ngTemplateOutlet="formBody"></ng-container>
    </app-component-card>
  </div>
</ng-template>

<ng-template #formBody>
  <div [class.p-6]="drawerMode">
    @if (loading) {
      <div class="flex justify-center items-center py-12">
        <div
          class="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-500"
        ></div>
        <span class="ml-3 text-gray-500 dark:text-gray-400"
          >Cargando datos...</span
        >
      </div>
    }

    @if (error) {
      <div
        class="mb-4 p-4 bg-error-50 border border-error-200 rounded-lg dark:bg-error-500/10 dark:border-error-500/20"
      >
        <p class="text-sm text-error-600 dark:text-error-400">{{ error }}</p>
      </div>
    }

    @if (!loading) {
      <form
        [formGroup]="form"
        (ngSubmit)="guardar()"
        class="space-y-5"
      >
        <!-- ===== Campos principales de la lista ===== -->
        <div class="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div class="sm:col-span-2">
            <label
              class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Nombre <span class="text-error-500">*</span>
            </label>
            <input
              type="text"
              formControlName="name"
              placeholder="Nombre de la lista"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            />
            @if (hasError('name')) {
              <p class="mt-1.5 text-sm text-error-500">
                {{ getError('name') }}
              </p>
            }
          </div>

          <div class="sm:col-span-2">
            <label
              class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Descripción</label
            >
            <textarea
              formControlName="description"
              rows="2"
              placeholder="Descripción opcional"
              class="dark:bg-dark-900 w-full rounded-lg border bg-transparent px-4 py-2.5 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
              [class.border-error-500]="
                hasError('description') || descriptionLength > 500
              "
              [class.border-gray-300]="
                !hasError('description') && descriptionLength <= 500
              "
            ></textarea>

            <!-- Mensaje de error + contador en la misma fila -->
            <div class="mt-1.5 flex items-start justify-between gap-3">
              <div class="flex-1">
                @if (hasError('description')) {
                  <p class="text-sm text-error-500">
                    {{ getError('description') }}
                  </p>
                } @else if (descriptionLength > 500) {
                  <p class="text-sm text-error-500">
                    La descripción no puede superar los 500 caracteres.
                  </p>
                }
              </div>
              <p
                class="text-xs tabular-nums whitespace-nowrap"
                [class.text-gray-500]="descriptionLength <= 450"
                [class.dark:text-gray-400]="descriptionLength <= 450"
                [class.text-warning-500]="
                  descriptionLength > 450 && descriptionLength <= 500
                "
                [class.text-error-500]="descriptionLength > 500"
              >
                {{ descriptionLength }} / 500
              </p>
            </div>
          </div>

          <div>
            <label
              class="mb-1.5 block text-sm font-medium text-gray-700 dark:text-gray-300"
              >Moneda</label
            >
            <select
              formControlName="currencyId"
              class="dark:bg-dark-900 h-11 w-full rounded-lg border border-gray-300 bg-transparent px-4 py-2.5 text-sm text-gray-800 dark:border-gray-700 dark:bg-gray-900 dark:text-white/90"
            >
              <option [ngValue]="null">Moneda por defecto</option>
              @for (c of currencies; track c.id) {
                <option [ngValue]="c.id">{{ c.code }} - {{ c.name }}</option>
              }
            </select>
          </div>

          <div class="flex items-start gap-3 pt-7">
            <input
              type="checkbox"
              formControlName="isDefault"
              id="isDefault"
              class="mt-0.5 h-4 w-4 rounded border-gray-300 text-brand-500 focus:ring-brand-500/10 dark:border-gray-600 dark:bg-gray-800"
            />
            <label
              for="isDefault"
              class="text-sm font-medium text-gray-700 dark:text-gray-300"
            >
              Lista predeterminada
              <p class="text-xs text-gray-500 dark:text-gray-400 font-normal">
                Se usará cuando ningún cliente tenga lista asignada
              </p>
            </label>
          </div>
        </div>

        <!-- ===== Items de la lista (siempre visible) ===== -->
        <div class="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <div class="flex items-center justify-between mb-4">
            <h3 class="text-sm font-semibold text-gray-700 dark:text-gray-300">
              Productos de la lista
              @if (items.length > 0) {
                <span
                  class="ml-2 text-xs font-normal text-brand-600 dark:text-brand-400"
                >
                  (los cambios se guardarán al hacer clic en
                  {{ isEditing ? 'Actualizar' : 'Crear Lista' }})
                </span>
              }
            </h3>
            @if (!showItemForm) {
              <button
                type="button"
                (click)="abrirItemForm()"
                class="px-3 py-1.5 text-xs rounded-lg font-medium bg-brand-500 text-white hover:bg-brand-600"
              >
                + Agregar producto
              </button>
            }
          </div>

          <!-- Form inline para agregar/editar item -->
          @if (showItemForm) {
            <div
              class="mb-4 p-4 bg-gray-50 dark:bg-gray-800/40 border border-gray-200 dark:border-gray-700 rounded-lg space-y-3"
              [formGroup]="itemForm"
            >
              <p class="text-xs font-semibold text-gray-600 dark:text-gray-400">
                {{ itemEnEdicion ? 'Editar producto' : 'Agregar producto' }}
              </p>
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div class="sm:col-span-2">
                  <label
                    class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
                    >Producto <span class="text-error-500">*</span></label
                  >
                  <div class="relative">
                    <input
                      type="text"
                      [(ngModel)]="productSearch"
                      [ngModelOptions]="{ standalone: true }"
                      (ngModelChange)="buscarProductos()"
                      (focus)="onFocusProducto()"
                      (blur)="onBlurProducto()"
                      placeholder="Buscar producto..."
                      class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    />
                    @if (productResults.length > 0) {
                      <div
                        class="absolute z-10 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-md shadow-lg max-h-40 overflow-y-auto"
                      >
                        @for (p of productResults; track p.id) {
                          <button
                            type="button"
                            (click)="seleccionarProducto(p)"
                            class="w-full text-left px-3 py-2 text-xs hover:bg-brand-50 dark:hover:bg-brand-900/20"
                          >
                            <span
                              class="font-medium text-gray-800 dark:text-white"
                              >{{ p.name }}</span
                            >
                            <span class="text-gray-400 ml-2"
                              >({{ p.sku }}) -
                              {{ formatPrecio(p.price, p.currencyCode) }}</span
                            >
                          </button>
                        }
                      </div>
                    }
                  </div>
                </div>

                <div>
                  <label
                    class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
                    >Tipo de precio</label
                  >
                  <select
                    formControlName="priceType"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                  >
                    <option value="fixed">Precio fijo</option>
                    <option value="discount_percent">Descuento %</option>
                    <option value="discount_amount">Descuento monto</option>
                  </select>
                </div>

                @if (itemForm.get('priceType')?.value === 'fixed') {
                  <div>
                    <label
                      class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
                      >Precio fijo</label
                    >
                    <input
                      type="number"
                      formControlName="price"
                      min="0"
                      step="0.01"
                      class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      [class.border-error-500]="
                        itemForm.get('price')?.invalid &&
                        itemForm.get('price')?.touched
                      "
                      [class.border-gray-300]="
                        !(
                          itemForm.get('price')?.invalid &&
                          itemForm.get('price')?.touched
                        )
                      "
                    />
                    @if (
                      itemForm.get('price')?.invalid &&
                      itemForm.get('price')?.touched
                    ) {
                      <p class="mt-1 text-xs text-error-500">
                        El precio debe ser mayor a 0.
                      </p>
                    }
                  </div>
                }
                @if (itemForm.get('priceType')?.value === 'discount_percent') {
                  <div>
                    <label
                      class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
                      >Descuento %</label
                    >
                    <input
                      type="number"
                      formControlName="discountPercent"
                      min="0"
                      max="100"
                      step="0.01"
                      class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      [class.border-error-500]="
                        itemForm.get('discountPercent')?.invalid &&
                        itemForm.get('discountPercent')?.touched
                      "
                      [class.border-gray-300]="
                        !(
                          itemForm.get('discountPercent')?.invalid &&
                          itemForm.get('discountPercent')?.touched
                        )
                      "
                    />
                    @if (
                      itemForm.get('discountPercent')?.invalid &&
                      itemForm.get('discountPercent')?.touched
                    ) {
                      <p class="mt-1 text-xs text-error-500">
                        El descuento debe ser entre 0.01% y 100%.
                      </p>
                    }
                  </div>
                }
                @if (itemForm.get('priceType')?.value === 'discount_amount') {
                  <div>
                    <label
                      class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
                      >Descuento monto</label
                    >
                    <input
                      type="number"
                      formControlName="discountAmount"
                      min="0"
                      step="0.01"
                      class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                      [class.border-error-500]="
                        itemForm.get('discountAmount')?.invalid &&
                        itemForm.get('discountAmount')?.touched
                      "
                      [class.border-gray-300]="
                        !(
                          itemForm.get('discountAmount')?.invalid &&
                          itemForm.get('discountAmount')?.touched
                        )
                      "
                    />
                    @if (
                      itemForm.get('discountAmount')?.invalid &&
                      itemForm.get('discountAmount')?.touched
                    ) {
                      <p class="mt-1 text-xs text-error-500">
                        El descuento debe ser mayor a 0.
                      </p>
                    }
                  </div>
                }

                <div>
                  <label
                    class="block text-xs font-medium text-gray-600 dark:text-gray-400 mb-1"
                    >Cantidad mínima</label
                  >
                  <input
                    type="number"
                    formControlName="minQuantity"
                    min="0"
                    class="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                    [class.border-error-500]="
                      itemForm.get('minQuantity')?.invalid &&
                      itemForm.get('minQuantity')?.touched
                    "
                    [class.border-gray-300]="
                      !(
                        itemForm.get('minQuantity')?.invalid &&
                        itemForm.get('minQuantity')?.touched
                      )
                    "
                  />
                  @if (
                    itemForm.get('minQuantity')?.invalid &&
                    itemForm.get('minQuantity')?.touched
                  ) {
                    <p class="mt-1 text-xs text-error-500">
                      La cantidad mínima debe ser al menos 1.
                    </p>
                  }
                </div>
              </div>

              @if (selectedProduct) {
                <p class="text-xs text-gray-500 dark:text-gray-400">
                  Precio base del producto:
                  <span class="font-medium text-gray-700 dark:text-gray-300">{{
                    formatPrecio(
                      selectedProduct.price,
                      selectedProduct.currencyCode
                    )
                  }}</span>
                </p>
              }
              @if (resolvedPreview !== null) {
                <p class="text-xs text-gray-600 dark:text-gray-400">
                  Precio resuelto:
                  <span
                    class="font-semibold text-brand-600 dark:text-brand-400"
                    >{{ formatPrecio(resolvedPreview, monedaListaCode) }}</span
                  >
                </p>
              }

              <div class="flex items-center justify-end gap-2">
                <button
                  type="button"
                  (click)="cerrarItemForm()"
                  class="px-3 py-1.5 text-xs rounded-lg font-medium bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  (click)="guardarItem()"
                  class="px-3 py-1.5 text-xs rounded-lg font-medium bg-brand-500 text-white hover:bg-brand-600"
                >
                  {{ itemEnEdicion ? 'Actualizar item' : 'Guardar item' }}
                </button>
              </div>
            </div>
          }

          <!-- Tabla de items -->
          @if (loadingItems) {
            <div class="flex justify-center py-6">
              <div
                class="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-500"
              ></div>
            </div>
          } @else if (items.length === 0) {
            <p
              class="text-center text-sm text-gray-500 dark:text-gray-400 py-6"
            >
              Sin productos en esta lista.
            </p>
          } @else {
            <div
              class="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700"
            >
              <table class="min-w-full text-sm">
                <thead class="bg-gray-50 dark:bg-gray-800/60">
                  <tr>
                    <th
                      class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      Producto
                    </th>
                    <th
                      class="px-3 py-2 text-left text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      Tipo
                    </th>
                    <th
                      class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      Precio base
                    </th>
                    <th
                      class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      Config.
                    </th>
                    <th
                      class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      Precio lista
                    </th>
                    <th
                      class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      Min. cant.
                    </th>
                    <th
                      class="px-3 py-2 text-right text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      Total mín.
                    </th>
                    <th
                      class="px-3 py-2 text-center text-xs font-medium text-gray-500 dark:text-gray-400"
                    >
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody class="divide-y divide-gray-100 dark:divide-white/5">
                  @for (it of items; track it.id) {
                    <tr>
                      <!-- Producto -->
                      <td class="px-3 py-2 text-gray-700 dark:text-gray-300">
                        <div class="flex flex-col">
                          <span class="text-sm font-medium">{{
                            getProductName(it.productId)
                          }}</span>
                          @if (getProductSku(it.productId)) {
                            <span
                              class="text-[10px] font-mono text-gray-400 dark:text-gray-500"
                              >{{ getProductSku(it.productId) }}</span
                            >
                          }
                        </div>
                      </td>

                      <!-- Tipo -->
                      <td
                        class="px-3 py-2 text-xs text-gray-500 dark:text-gray-400"
                      >
                        {{ getPriceTypeLabel(it.priceType) }}
                      </td>

                      <!-- Precio base -->
                      <td
                        class="px-3 py-2 text-right text-gray-500 dark:text-gray-400"
                      >
                        {{
                          formatPrecio(
                            getProductBasePrice(it.productId),
                            getProductCurrencyCode(it.productId)
                          )
                        }}
                      </td>

                      <!-- Config. (lo que ingresó: precio fijo, % o monto) -->
                      <td
                        class="px-3 py-2 text-right text-gray-700 dark:text-gray-300"
                      >
                        @if (it.priceType === 'fixed') {
                          {{ formatPrecio(it.price, monedaListaCode) }}
                        } @else if (it.priceType === 'discount_percent') {
                          {{ it.discountPercent }}%
                        } @else if (it.priceType === 'discount_amount') {
                          -{{
                            formatPrecio(it.discountAmount, monedaListaCode)
                          }}
                        }
                      </td>

                      <!-- Precio lista (resuelto) -->
                      <td
                        class="px-3 py-2 text-right font-semibold text-brand-600 dark:text-brand-400"
                      >
                        {{ formatPrecio(it.resolvedPrice, monedaListaCode) }}
                      </td>

                      <!-- Min. cant. -->
                      <td
                        class="px-3 py-2 text-right text-xs text-gray-500 dark:text-gray-400"
                      >
                        {{ it.minQuantity ?? '-' }}
                      </td>

                      <!-- Total mín. -->
                      <td
                        class="px-3 py-2 text-right text-gray-700 dark:text-gray-300"
                      >
                        {{ formatPrecio(getItemTotal(it), monedaListaCode) }}
                      </td>

                      <!-- Acciones -->
                      <td class="px-3 py-2 text-center">
                        <div class="flex items-center justify-center gap-1">
                          <button
                            type="button"
                            (click)="editarItem(it)"
                            class="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-400 hover:text-gray-600"
                            title="Editar"
                          >
                            <svg
                              class="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                              />
                            </svg>
                          </button>
                          <button
                            type="button"
                            (click)="eliminarItem(it)"
                            class="rounded p-1 hover:bg-error-50 hover:text-error-600 text-gray-400"
                            title="Eliminar"
                          >
                            <svg
                              class="w-3.5 h-3.5"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                              ></path>
                            </svg>
                          </button>
                        </div>
                      </td>
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        </div>

        <!-- ===== Botones de la lista (al final, después de items) ===== -->
        <div
          class="flex items-center justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700"
        >
          <app-button
            variant="outline"
            type="button"
            (btnClick)="cancelar()"
            >Cancelar</app-button
          >
          <app-button
            variant="primary"
            type="button"
            (btnClick)="guardar()"
            [disabled]="saving || form.invalid"
          >
            {{
              saving ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear Lista'
            }}
          </app-button>
        </div>
      </form>
    }
  </div>
</ng-template>
